/* Widget-level actions: CSV export and full screen.
 *
 * Download used to live only in the editor toolbar (Editor/index.js handleDownloadCSV),
 * which meant a published board had no way to export at all - the board toolbar had an
 * Export button but its handler had no branch for it. Now that "Allow download" is a
 * per-widget property on a dashboard, every widget header needs the same routine, so it
 * lives here and the editor calls it too rather than keeping a second copy.
 */

/**
 * Serialize a cell value for CSV.
 * - Primitives → as-is
 * - Arrays of objects → "{ Key: Val; Key: Val } | { Key: Val; Key: Val }"
 * - Plain objects → "Key: Val; Key: Val"
 * - Arrays of primitives → pipe-joined
 */
export const serializeCellValue = (val) => {
  if (val === null || val === undefined) return "";

  // primitive
  if (typeof val !== "object") return String(val);

  // array
  if (Array.isArray(val)) {
    if (val.length === 0) return "";

    return val
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item !== "object") return String(item);

        const itemEntries = Object.entries(item);
        return `{ ${itemEntries.map(([k, v]) => `${k}: ${serializeCellValue(v)}`).join("; ")} }`;
      })
      .join(" | ");
  }

  // plain object
  const entries = Object.entries(val);
  if (entries.length === 0) return "";

  return entries.map(([k, v]) => `${k}: ${serializeCellValue(v)}`).join("; ");
};

/* Every field is quoted and embedded quotes are doubled, so a value containing a comma
   or a line break does not shift the following columns. */
export const buildCsvString = (headers, rows) =>
  [
    headers.join(","),
    ...rows.map((row) =>
      headers
        .map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`)
        .join(","),
    ),
  ].join("\n");

export const triggerCsvDownload = (csvString, fileName) => {
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const stamp = () => new Date().toISOString().slice(0, 10);

/* Everything below exports what the user configured, not the raw API payload: hidden
   columns are left out and headers use the display name. An export that did not match
   the table on screen would look like the wrong data. */

/**
 * Table widget → one row per record, one column per checked field.
 * Returns false when there was nothing to write, so the caller can say why.
 */
export const exportRowsToCsv = ({ rows, fields, widgetName }) => {
  const visibleFields = (fields || []).filter((f) => f.checked !== false);
  const dataRows = Array.isArray(rows) ? rows : [];
  if (!visibleFields.length || !dataRows.length) return false;

  const headers = visibleFields.map((f) => f.fieldDisplayName || f.fieldName);
  const csvRows = dataRows.map((row) => {
    const csvRow = {};
    visibleFields.forEach((f) => {
      csvRow[f.fieldDisplayName || f.fieldName] = serializeCellValue(row[f.fieldName] ?? "");
    });
    return csvRow;
  });

  triggerCsvDownload(buildCsvString(headers, csvRows), `${widgetName || "report"}_${stamp()}.csv`);
  return true;
};

/**
 * Chart widget → the plotted series, not the underlying rows: the group-by field and the
 * summed value field, grouped exactly the way ChartContainer groups them for display.
 */
export const exportChartToCsv = ({ rows, groupByField, valueField, widgetName }) => {
  const dataRows = Array.isArray(rows) ? rows : rows?.Result || rows?.data || [];
  if (!groupByField || !valueField || !dataRows.length) return false;

  const grouped = dataRows.reduce((acc, row) => {
    const key = row[groupByField];
    if (!acc[key]) acc[key] = 0;
    acc[key] += Number(row[valueField] || 0);
    return acc;
  }, {});

  const headers = [groupByField, valueField];
  const csvRows = Object.entries(grouped).map(([name, value]) => ({
    [groupByField]: name,
    [valueField]: value,
  }));
  if (!csvRows.length) return false;

  triggerCsvDownload(buildCsvString(headers, csvRows), `${widgetName || "chart"}_${stamp()}.csv`);
  return true;
};

/* Full screen for a single widget. The board-level button fullscreens the whole board
   through react-easyfullscreen; a widget has no such wrapper, so it goes straight to the
   browser API on its own element.

   The re-measure afterwards is not optional: react-grid-layout and ApexCharts only size
   themselves on mount and on window resize, and the resize that entering fullscreen
   fires lands before the new size is readable - without this the widget renders
   fullscreen at its old pixel width. */
export const toggleElementFullScreen = (element) => {
  if (!element) return;

  const current = document.fullscreenElement || document.webkitFullscreenElement;
  if (current) {
    const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
    if (exit) Promise.resolve(exit.call(document)).catch((err) => console.error("Fullscreen error:", err));
    return;
  }

  const request = element.requestFullscreen || element.webkitRequestFullscreen || element.msRequestFullscreen;
  if (!request) {
    console.warn("Full screen is not supported in this browser");
    return;
  }
  Promise.resolve(request.call(element))
    .then(() => setTimeout(() => window.dispatchEvent(new Event("resize")), 100))
    .catch((err) => console.error("Fullscreen error:", err));
};
